import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Checkbox,
    Container,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
    IconDeviceFloppy,
    IconSearch,
    IconSettings,
} from "@tabler/icons-react";

type EventDto = {
    eventId: number;
    eventName: string;
    name?: string;
};

type AccessRow = {
    functionId: number;
    functionName: string;
    canSeeExperience: boolean;
    canSeeTransport: boolean;
};

const apiUrl = process.env.REACT_APP_API_URL;

export default function EventOverviewFeatureAccessAdminPage() {
    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<string | null>(null);
    const [rows, setRows] = useState<AccessRow[]>([]);
    const [search, setSearch] = useState("");

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingRows, setLoadingRows] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const selectedEventId = eventId ? Number(eventId) : null;

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();

        if (!q) return rows;

        return rows.filter((x) =>
            `${x.functionName} ${x.functionId}`.toLowerCase().includes(q)
        );
    }, [rows, search]);

    async function loadEvents() {
        if (!apiUrl) return;

        setLoadingEvents(true);
        setError(null);

        try {
            const res = await fetch(`${apiUrl}/api/events`);

            if (!res.ok) throw new Error("Kunne ikke hente events");

            const data = await res.json();
            const list = Array.isArray(data) ? data : data.items ?? [];

            const mapped: EventDto[] = list.map((e: any) => ({
                eventId: e.eventId ?? e.EventId ?? e.eventID ?? e.EventID,
                eventName:
                    e.eventName ??
                    e.EventName ??
                    e.name ??
                    e.Name ??
                    `Event ${e.eventId ?? e.EventId}`,
            }));

            setEvents(mapped);

            if (!eventId && mapped.length > 0) {
                setEventId(String(mapped[0].eventId));
            }
        } catch (err: any) {
            setError(err.message ?? "Fejl ved hentning af events");
        } finally {
            setLoadingEvents(false);
        }
    }

    async function loadAccess() {
        if (!apiUrl || !selectedEventId) return;

        setLoadingRows(true);
        setError(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/events/${selectedEventId}/person-overview/admin/access`
            );

            if (!res.ok) throw new Error("Kunne ikke hente adgangsregler");

            const data = await res.json();

            const mapped: AccessRow[] = (Array.isArray(data) ? data : []).map((x: any) => ({
                functionId: x.functionId ?? x.FunctionId,
                functionName: x.functionName ?? x.FunctionName ?? "",
                canSeeExperience: Boolean(x.canSeeExperience ?? x.CanSeeExperience),
                canSeeTransport: Boolean(x.canSeeTransport ?? x.CanSeeTransport),
            }));

            setRows(mapped);
        } catch (err: any) {
            setError(err.message ?? "Fejl ved hentning af adgangsregler");
            setRows([]);
        } finally {
            setLoadingRows(false);
        }
    }

    function updateRow(
        functionId: number,
        field: "canSeeExperience" | "canSeeTransport",
        value: boolean
    ) {
        setRows((prev) =>
            prev.map((row) =>
                row.functionId === functionId ? { ...row, [field]: value } : row
            )
        );
    }

    async function save() {
        if (!apiUrl || !selectedEventId) return;

        setSaving(true);
        setError(null);

        try {
            const payload = rows.map((x) => ({
                functionId: x.functionId,
                canSeeExperience: x.canSeeExperience,
                canSeeTransport: x.canSeeTransport,
            }));

            const res = await fetch(
                `${apiUrl}/api/events/${selectedEventId}/person-overview/admin/access`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!res.ok) throw new Error("Kunne ikke gemme adgangsregler");

            showNotification({
                title: "Gemt",
                message: "Adgangsreglerne er opdateret.",
                color: "green",
            });

            await loadAccess();
        } catch (err: any) {
            setError(err.message ?? "Fejl ved gemning");
        } finally {
            setSaving(false);
        }
    }

    function setAll(field: "canSeeExperience" | "canSeeTransport", value: boolean) {
        const visibleIds = new Set(filteredRows.map((x) => x.functionId));

        setRows((prev) =>
            prev.map((row) =>
                visibleIds.has(row.functionId) ? { ...row, [field]: value } : row
            )
        );
    }

    useEffect(() => {
        loadEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEventId]);

    return (
        <Container fluid py="xl">
            <Group justify="center">
                <div style={{ width: "70%", minWidth: 900 }}>
                    <Stack gap="md">

                        {/* Header */}
                        <Group justify="space-between" align="center">
                            <div>
                                <Title order={2}>Person overview adgang</Title>
                                <Text c="dimmed">
                                    Styr hvilke funktioner der må se Experience og Transport.
                                </Text>
                            </div>

                            <Button
                                color="red"
                                radius="md"
                                onClick={save}
                                loading={saving}
                                disabled={!selectedEventId}
                                leftSection={<IconDeviceFloppy size={18} />}
                            >
                                Gem
                            </Button>
                        </Group>

                        {/* Error */}
                        {error && (
                            <Alert color="red" title="Fejl">
                                {error}
                            </Alert>
                        )}

                        {/* Filters */}
                        <Group grow align="end">
                            <Select
                                label="Event"
                                placeholder="Vælg event"
                                value={eventId}
                                onChange={setEventId}
                                disabled={loadingEvents}
                                data={events.map((e) => ({
                                    value: String(e.eventId),
                                    label: e.eventName,
                                }))}
                            />

                            <TextInput
                                label="Søg funktion"
                                placeholder="Søg funktion eller ID..."
                                leftSection={<IconSearch size={16} />}
                                value={search}
                                onChange={(e) => setSearch(e.currentTarget.value)}
                            />
                        </Group>

                        {/* Stats + quick actions */}
                        <Group justify="space-between">
                            <Group>
                                <Badge color="red" variant="light">
                                    {filteredRows.length} funktioner
                                </Badge>

                                <Badge color="green" variant="light">
                                    Experience: {rows.filter((x) => x.canSeeExperience).length}
                                </Badge>

                                <Badge color="blue" variant="light">
                                    Transport: {rows.filter((x) => x.canSeeTransport).length}
                                </Badge>
                            </Group>

                            <Group>
                                <Button size="xs" color="red" onClick={() => setAll("canSeeExperience", true)}>
                                    Experience til
                                </Button>
                                <Button size="xs" color="gray" variant="outline" onClick={() => setAll("canSeeExperience", false)}>
                                    Experience fra
                                </Button>
                                <Button size="xs" color="red" onClick={() => setAll("canSeeTransport", true)}>
                                    Transport til
                                </Button>
                                <Button size="xs" color="gray" variant="outline" onClick={() => setAll("canSeeTransport", false)}>
                                    Transport fra
                                </Button>
                            </Group>
                        </Group>

                        {/* Table */}
                        {loadingRows ? (
                            <Group justify="center" py="xl">
                                <Loader color="red" />
                            </Group>
                        ) : (
                            <Table striped highlightOnHover withTableBorder withColumnBorders>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>ID</Table.Th>
                                        <Table.Th>Funktion navn</Table.Th>
                                        <Table.Th>Experience</Table.Th>
                                        <Table.Th>Transport</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>

                                <Table.Tbody>
                                    {filteredRows.map((row) => (
                                        <Table.Tr key={row.functionId}>
                                            <Table.Td>{row.functionId}</Table.Td>

                                            <Table.Td>
                                                <Text fw={700}>{row.functionName}</Text>
                                            </Table.Td>

                                            <Table.Td>
                                                <Checkbox
                                                    color="red"
                                                    checked={row.canSeeExperience}
                                                    onChange={(e) =>
                                                        updateRow(
                                                            row.functionId,
                                                            "canSeeExperience",
                                                            e.currentTarget.checked
                                                        )
                                                    }
                                                />
                                            </Table.Td>

                                            <Table.Td>
                                                <Checkbox
                                                    color="red"
                                                    checked={row.canSeeTransport}
                                                    onChange={(e) =>
                                                        updateRow(
                                                            row.functionId,
                                                            "canSeeTransport",
                                                            e.currentTarget.checked
                                                        )
                                                    }
                                                />
                                            </Table.Td>
                                        </Table.Tr>
                                    ))}

                                    {filteredRows.length === 0 && (
                                        <Table.Tr>
                                            <Table.Td colSpan={4}>
                                                <Text c="dimmed" ta="center" py="xl">
                                                    Ingen funktioner fundet.
                                                </Text>
                                            </Table.Td>
                                        </Table.Tr>
                                    )}
                                </Table.Tbody>
                            </Table>
                        )}
                    </Stack>
                </div>
            </Group>
        </Container>
    );
}